export type ScraperItemStatus = "pending" | "empty" | "with_passages" | "error";

export interface ScraperPassage {
  location: string;
  dateLabel: string;
  price: string;
  concessionaireName?: string;
}

export interface ScraperConsultationItem {
  id: string;
  name: string;
  status: ScraperItemStatus;
  paymentUrl?: string;
  passageCount: number;
  totalPrice?: string;
  passages: ScraperPassage[];
}

export interface ScrapersConsultation {
  items: ScraperConsultationItem[];
  completedCount: number;
  total: number;
  isComplete: boolean;
  hasAnyPassage: boolean;
  hasConsultationError: boolean;
  totalPassageCount: number;
}

export interface BuildScrapersConsultationOptions {
  total?: number;
  completedCount?: number;
  isComplete?: boolean;
  hasConsultationError?: boolean;
}

export function createScraperConsultationItem(input: {
  id: string;
  name: string;
  status: ScraperItemStatus;
  paymentUrl?: string;
  passages?: ScraperPassage[];
  totalPrice?: string;
}): ScraperConsultationItem {
  const passages = input.passages ?? [];
  return {
    id: input.id,
    name: input.name,
    status: input.status,
    paymentUrl: input.paymentUrl,
    passageCount: passages.length,
    totalPrice: input.totalPrice,
    passages,
  };
}

export function buildScrapersConsultation(
  items: ScraperConsultationItem[],
  options: BuildScrapersConsultationOptions = {},
): ScrapersConsultation {
  const completedFromItems = items.filter(
    (item) => item.status !== "pending",
  ).length;
  const completedCount = options.completedCount ?? completedFromItems;
  const total = options.total ?? items.length;
  const hasConsultationError =
    options.hasConsultationError ??
    (items.length > 0 && items.every((item) => item.status === "error"));
  const isComplete =
    options.isComplete ??
    (items.length > 0 &&
      completedFromItems === items.length &&
      completedCount === total);
  const totalPassageCount = items.reduce(
    (acc, item) => acc + item.passageCount,
    0,
  );

  return {
    items,
    completedCount,
    total,
    isComplete,
    hasAnyPassage: items.some((item) => item.passageCount > 0),
    hasConsultationError,
    totalPassageCount,
  };
}

export function createPendingConsultation(): ScrapersConsultation {
  return buildScrapersConsultation([], { isComplete: false });
}

export function createFailedConsultation(): ScrapersConsultation {
  return buildScrapersConsultation([], {
    isComplete: true,
    hasConsultationError: true,
  });
}

export function createEmptyConsultation(): ScrapersConsultation {
  return buildScrapersConsultation([], {
    isComplete: true,
    hasConsultationError: false,
  });
}

export function parseBrlAmount(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string" || !value.trim()) return undefined;
  const parsed = Number(value.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function formatBrlPrice(value: number): string {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function sumPassagePrices(
  passages: ScraperPassage[],
): string | undefined {
  if (passages.length === 0) return undefined;
  const total = passages.reduce((acc, item) => {
    const parsed = parseBrlAmount(item.price);
    return parsed === undefined ? acc : acc + parsed;
  }, 0);
  return formatBrlPrice(total);
}

export function createDemoScraperPassages(
  count: number,
  price = "47,00",
): ScraperPassage[] {
  return Array.from({ length: count }, () => ({
    location: "Rod. Presidente Dutra",
    dateLabel: "15.08.2025 Manhã",
    price,
  }));
}
